import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PickupSlotCode, SlotBoardRow } from '../../services/pickupSlots.api';
import { relaySlotResponse } from '../../services/pickupSlots.api';
import { toDatetimeLocalValue } from '../../utils/pickupSlotStatus';

interface Props {
  row: SlotBoardRow;
  onClose: () => void;
}

const SLOT_CODES: PickupSlotCode[] = ['S1', 'S2', 'S3', 'S4', 'S5'];

/** Officer relays a merchant's out-of-app rollover reply (WhatsApp/phone). */
export default function SlotResponseModal({ row, onClose }: Props) {
  const [choice, setChoice] = useState<'accept' | 'change' | 'defer'>('accept');
  const [slotCode, setSlotCode] = useState<PickupSlotCode>('S1');
  const [respondedAt, setRespondedAt] = useState(() => toDatetimeLocalValue(new Date()));
  const [channel, setChannel] = useState<'whatsapp' | 'push'>('whatsapp');
  const [preferredTiming, setPreferredTiming] = useState<'anytime' | 'early' | 'mid' | 'late'>('anytime');
  const [error, setError] = useState<string | null>(null);
  const [superseded, setSuperseded] = useState(false);
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () =>
      relaySlotResponse(row.pickup_uid, {
        choice,
        responded_at: new Date(respondedAt).toISOString(),
        channel,
        ...(choice === 'change'
          ? { time_slot_code: slotCode, preferred_timing: preferredTiming === 'anytime' ? null : preferredTiming }
          : {}),
      }),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['pickupSlotBoard'] });
      if (result.superseded) {
        setSuperseded(true); // newer in-app response exists — inform, don't close silently
      } else {
        onClose();
      }
    },
    onError: (err: { response?: { data?: { error?: string } } }) =>
      setError(err.response?.data?.error ?? 'Failed to relay the response'),
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white p-5 shadow-xl">
        <h2 className="text-lg font-semibold text-gray-900">Record merchant response</h2>
        <p className="mt-1 text-sm text-gray-500">
          {row.merchant_name} · {row.platform_code}
        </p>

        {superseded ? (
          <div className="mt-3 rounded-md bg-amber-50 p-3 text-sm text-amber-800">
            A newer in-app response already exists — this relay was recorded but superseded.
          </div>
        ) : (
          <div className="mt-3 space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600">Merchant's choice</label>
              <div className="mt-1 flex gap-2">
                {(['accept', 'change', 'defer'] as const).map((c) => (
                  <button
                    key={c}
                    onClick={() => setChoice(c)}
                    className={`rounded-md border px-3 py-1.5 text-sm capitalize ${
                      choice === c ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300'
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
            {choice === 'change' && (
              <>
                <div>
                  <label className="block text-xs font-medium text-gray-600">New slot</label>
                  <select
                    value={slotCode}
                    onChange={(e) => setSlotCode(e.target.value as PickupSlotCode)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                  >
                    {SLOT_CODES.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600">Preferred timing (optional)</label>
                  <select
                    value={preferredTiming}
                    onChange={(e) => setPreferredTiming(e.target.value as typeof preferredTiming)}
                    className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
                  >
                    <option value="anytime">Anytime</option>
                    <option value="early">Early</option>
                    <option value="mid">Mid</option>
                    <option value="late">Late</option>
                  </select>
                </div>
              </>
            )}
            <div>
              <label className="block text-xs font-medium text-gray-600">Merchant replied at</label>
              <input
                type="datetime-local"
                value={respondedAt}
                onChange={(e) => setRespondedAt(e.target.value)}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600">Channel</label>
              <select
                value={channel}
                onChange={(e) => setChannel(e.target.value as 'whatsapp' | 'push')}
                className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              >
                <option value="whatsapp">WhatsApp</option>
                <option value="push">Push</option>
              </select>
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-md border border-gray-300 px-3 py-1.5 text-sm">
            {superseded ? 'Close' : 'Cancel'}
          </button>
          {!superseded && (
            <button
              onClick={() => mutation.mutate()}
              disabled={mutation.isPending}
              className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {mutation.isPending ? 'Relaying…' : 'Relay response'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
